/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { Group, Scale } from '../utils/UNSAFE_visTypes/chart';
import { getLineAreaSegments } from '#utils/PRIVATE_chartUtils/utils';
import { LineSeries } from '#PRIVATE_LineAreaComponents/LineSeries';

type VariedLineRefObjProps = {
  id: string;
  xScale: Scale;
  yScale: Scale;
  items: { value: number }[];
  orientation?: 'horizontal' | 'vertical';
  lineColor?: string;
  lineStyle?: 'solid' | 'dashed' | 'dotted';
  lineWidth?: number;
  accessibleLabel?: string;
  activeId?: string;
  groups?: Group[];
  xStartIndex?: number;
  xEndIndex?: number;
  isLog?: boolean;
  seriesIndex?: number;
};

export function VariedLineRefObj({
  id,
  xScale,
  yScale,
  items,
  orientation = 'horizontal',
  lineColor,
  lineWidth,
  groups = [],
  xStartIndex = 0,
  xEndIndex = 0,
  isLog = false,
  seriesIndex = 0,
  lineStyle,
  accessibleLabel,
  activeId
}: VariedLineRefObjProps) {
  if (!items || items.length === 0 || !xScale || !yScale) {
    return null;
  }

  const isHorizontal = orientation === 'horizontal';

  const valueCoords = getLineAreaSegments(
    yScale,
    xScale,
    items.map((item) => item.value),
    xStartIndex,
    xEndIndex,
    isLog,
    groups,
    0,
    (_, gIndex) => ({ id: `${id}_${gIndex}`, ...items[gIndex] })
  );

  return (
    <LineSeries
      id={id}
      activeId={activeId}
      accessibleLabel={accessibleLabel}
      lineStyle={lineStyle}
      lineType="straight"
      lineWidth={lineWidth}
      color={lineColor}
      variant="refLine"
      lineSegmentCoords={valueCoords}
      isHorizontal={isHorizontal}
      isHighlighted={false}
      seriesIndex={seriesIndex}
    />
  );
}
